import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, apiFetch, apiFetchJson } from '../../lib/apiFetch.js';
import type { InvoiceDetailResponse } from './contractsMirror.js';

/**
 * TanStack Query hook for the COARSE invoice-detail endpoint
 * (`GET /api/v1/invoices/:id`, 21-02). One query key per invoice id.
 *
 * CRITICAL (Pitfall 5 / T-21-04): this query intentionally leaves the default
 * staleTime (0) — no aggressive freeze on the cached detail. The status badge
 * must reflect the LATEST server value on each load — the server derives status
 * read-only (it does not persist it on the GET), so a refetch after a payment is
 * recorded picks up the new Paid/Partial/Unpaid derivation.
 *
 * The request goes through `apiFetch` (the only fetch path); the SPA never sends
 * a tenant id (SEC-11) — the server scopes by the auth principal.
 */
export function invoiceQueryKey(id: number): readonly unknown[] {
  return ['invoice', id] as const;
}

export function useInvoiceQuery(id: number, enabled = true) {
  return useQuery<InvoiceDetailResponse>({
    queryKey: invoiceQueryKey(id),
    queryFn: () => apiFetchJson<InvoiceDetailResponse>(`/api/v1/invoices/${id}`),
    // `enabled` lets the edit form skip the fetch in create mode (id is absent).
    enabled: enabled && Number.isFinite(id) && id > 0,
  });
}

/**
 * Client-side mirror of the server's invoice-attachment constraints
 * (`DOCUMENT_ATTACHMENT_EXTENSIONS` / `DOCUMENT_ATTACHMENT_MIME_TYPES`,
 * `qelm-backend/workers/api/src/services/upload-validation.ts`) plus the
 * default `MAX_RECEIPT_UPLOAD_BYTES` the invoice-attachment route reuses
 * (`api.ts:1518`, `env.maxReceiptUploadBytes`). Rejecting a wrong-type or
 * oversized file here is a UX nicety, NOT the security boundary — the server
 * re-validates (signature-checked, `saveUpload`/`saveUploadBundle`) on every
 * upload regardless (T-27.1-02).
 */
export const INVOICE_ATTACHMENT_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'pdf', 'heic', 'heif'];
export const INVOICE_ATTACHMENT_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
  'application/pdf',
];
export const INVOICE_ATTACHMENT_ACCEPT = [
  ...INVOICE_ATTACHMENT_EXTENSIONS.map((ext) => `.${ext}`),
  ...INVOICE_ATTACHMENT_MIME_TYPES,
].join(',');
/** Mirrors the server's `MAX_RECEIPT_UPLOAD_BYTES` default (5 MB). */
export const MAX_INVOICE_ATTACHMENT_BYTES = 5 * 1024 * 1024;

export type InvoiceAttachmentValidationError = 'invalidType' | 'tooLarge';

/** Returns null when the file passes, or the reason code otherwise. */
export function validateInvoiceAttachmentFile(
  file: File,
): InvoiceAttachmentValidationError | null {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  const extensionOk = INVOICE_ATTACHMENT_EXTENSIONS.includes(extension);
  const mimeOk = !file.type || INVOICE_ATTACHMENT_MIME_TYPES.includes(file.type.toLowerCase());
  if (!extensionOk || !mimeOk) return 'invalidType';
  if (file.size > MAX_INVOICE_ATTACHMENT_BYTES) return 'tooLarge';
  return null;
}

async function postInvoiceAttachment(invoiceId: number, formData: FormData): Promise<void> {
  const response = await apiFetch(`/api/v1/invoices/${invoiceId}/attachment`, {
    method: 'POST',
    body: formData,
  });
  const body = (await response.json().catch(() => null)) as { ok?: boolean } | null;
  if (!response.ok || !body || body.ok === false) {
    throw new ApiError(response.status, 'attachment_upload_failed');
  }
}

/**
 * Imperative (non-hook) upload used by the CREATE form's two-step flow: the
 * invoice id does not exist until AFTER `POST /api/v1/invoices` succeeds, so a
 * hook-bound mutation (which needs the id at hook-creation/render time) cannot
 * be used there. Shares the same request shape as the mutation below.
 */
export async function uploadInvoiceAttachment(invoiceId: number, file: File): Promise<void> {
  const formData = new FormData();
  formData.set('attachment', file);
  await postInvoiceAttachment(invoiceId, formData);
}

/**
 * Upload (or replace) the invoice attachment (`POST /api/v1/invoices/:id/attachment`,
 * 22-21 — Admin/Manager, server-gated). The body is a `FormData` whose file field is
 * named `attachment`; the SPA never sends a tenant id (SEC-11). On success the invoice
 * detail query is invalidated so the attachment card + payments re-read server truth.
 * Rides `apiFetch` (double-submit CSRF on the non-safe method) — NOT `apiFetchJson`, so
 * the multipart body is passed through untouched (no JSON content-type forced).
 */
export function useUploadInvoiceAttachmentMutation(invoiceId: number) {
  const queryClient = useQueryClient();
  return useMutation<{ ok: boolean }, Error, FormData>({
    mutationFn: async (formData) => {
      await postInvoiceAttachment(invoiceId, formData);
      return { ok: true };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invoiceQueryKey(invoiceId) });
    },
  });
}

/**
 * Delete the invoice attachment (`DELETE /api/v1/invoices/:id/attachment`, 22-21).
 * Rides `apiFetch` (CSRF baked in); invalidates the detail query on success.
 */
export function useDeleteInvoiceAttachmentMutation(invoiceId: number) {
  const queryClient = useQueryClient();
  return useMutation<{ ok: boolean }, Error, void>({
    mutationFn: () =>
      apiFetchJson<{ ok: boolean }>(`/api/v1/invoices/${invoiceId}/attachment`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invoiceQueryKey(invoiceId) });
    },
  });
}
